import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Download, Loader, AlertCircle } from 'lucide-react'
import { api } from '../lib/api'

interface DocumentPreviewModalProps {
  isOpen: boolean
  onClose: () => void
  documentId: string
  documentTitle: string
  fileType: string
}

export function DocumentPreviewModal({ 
  isOpen, 
  onClose, 
  documentId, 
  documentTitle,
  fileType 
}: DocumentPreviewModalProps) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [previewBlob, setPreviewBlob] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && documentId) {
      setLoading(true)
      setError(null)
      
      // Use reference documents endpoint for all downloads
      api.get(`/api/reference-documents/${documentId}/download`, {
        responseType: 'blob'
      })
      .then(response => {
        const blob = new Blob([response.data], { type: response.headers['content-type'] })
        const url = URL.createObjectURL(blob)
        setPreviewBlob(url)
        setLoading(false)
      })
      .catch(err => {
        console.error('Failed to load document:', err)
        setError('Dokument konnte nicht geladen werden')
        setLoading(false)
      })
    }
    
    return () => {
      if (previewBlob) {
        URL.revokeObjectURL(previewBlob)
      }
    }
  }, [isOpen, documentId])

  const handleDownload = async () => {
    try {
      // Use reference documents endpoint for all downloads
      const response = await api.get(`/api/reference-documents/${documentId}/download`, {
        responseType: 'blob'
      })
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] })
      const url = URL.createObjectURL(blob)
      
      const link = document.createElement('a')
      link.href = url
      link.download = documentTitle
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Download failed:', err)
      setError('Download fehlgeschlagen')
    }
  }

  if (!isOpen) return null

  const isPDF = fileType === '.pdf'
  const isImage = ['.png', '.jpg', '.jpeg', '.gif'].includes(fileType.toLowerCase())
  const canPreview = isPDF || isImage

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-4 md:inset-10 bg-gray-900 rounded-xl shadow-2xl z-[10000] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="text-lg font-semibold truncate">{documentTitle}</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDownload}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Herunterladen"
                >
                  <Download className="w-5 h-5" />
                </button>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Schließen"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 relative overflow-hidden">
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader className="w-8 h-8 animate-spin" />
                </div>
              )}

              {canPreview && previewBlob ? (
                isPDF ? (
                  <embed
                    src={previewBlob}
                    type="application/pdf"
                    className="w-full h-full"
                  />
                ) : isImage ? (
                  <img
                    src={previewBlob}
                    alt={documentTitle}
                    className="w-full h-full object-contain"
                    onError={() => {
                      setError('Bild konnte nicht geladen werden')
                    }}
                  />
                ) : null
              ) : !canPreview ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <AlertCircle className="w-12 h-12 text-yellow-500 mb-4" />
                  <h4 className="text-lg font-medium mb-2">Vorschau nicht verfügbar</h4>
                  <p className="text-gray-400 mb-6">
                    Für diesen Dateityp ({fileType}) ist keine Vorschau möglich.
                  </p>
                  <button
                    onClick={handleDownload}
                    className="px-6 py-3 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Dokument herunterladen
                  </button>
                </div>
              ) : null}

              {error && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center p-8">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-red-400">{error}</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  )
}